/**
 * SegmentedControl Component
 *
 * A reusable single-selection component using segmented buttons.
 * Used for Role selection in the Edit User modal.
 *
 * Features:
 * - Segmented button group for single selection
 * - Visual indication of selected state
 * - Disabled state support
 * - Proper ARIA attributes for accessibility
 */

import React from 'react';
import {
  Button,
  Label,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';

export interface SegmentedControlOption {
  value: string;
  label: string;
}

interface SegmentedControlProps {
  /** Label for the control. When provided, displays above buttons and used for aria-label. */
  label?: string;
  /** Aria label when no visible label is desired. Falls back to label if not provided. */
  ariaLabel?: string;
  options: SegmentedControlOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  buttonGroup: {
    display: 'inline-flex',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  button: {
    borderRadius: 0,
    minWidth: '100px',
  },
  buttonFirst: {
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
  },
  buttonLast: {
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
  },
  buttonSelected: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  buttonUnselected: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
  },
});

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  label,
  ariaLabel,
  options,
  selectedValue,
  onChange,
  disabled = false,
  className,
}) => {
  const styles = useStyles();

  const handleClick = (value: string) => {
    if (disabled || value === selectedValue) return;
    onChange(value);
  };

  // Use ariaLabel if provided, otherwise fall back to label
  const groupAriaLabel = ariaLabel || label || 'Selection';

  return (
    <div className={styles.container}>
      {label && <Label className={styles.label}>{label}</Label>}
      <div
        role="group"
        aria-label={groupAriaLabel}
        className={mergeClasses(styles.buttonGroup, className)}
      >
        {options.map((option, index) => {
          const isSelected = option.value === selectedValue;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;

          return (
            <Button
              key={option.value}
              appearance={isSelected ? 'primary' : 'outline'}
              className={mergeClasses(
                styles.button,
                isFirst && styles.buttonFirst,
                isLast && styles.buttonLast,
                isSelected ? styles.buttonSelected : styles.buttonUnselected
              )}
              onClick={() => handleClick(option.value)}
              disabled={disabled}
              aria-pressed={isSelected}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
